/**
 * Componente para mostrar la lista de errores de archivos de participantes.
 *
 * Este componente renderiza una tabla con todos los errores de procesamiento
 * de archivos de carga masiva, incluyendo información del error, fila afectada
 * y acciones para ver detalles y exportar errores.
 *
 * @author Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 * @date [Fecha de creación: 2025-08-06]
 * @updated [Fecha de última modificación: 2025-08-11]
 * @updatedBy Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 */

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import PropTypes from "prop-types";
import { Pagination, Stack, Chip, Card, Grid, LinearProgress } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import GetAppIcon from "@mui/icons-material/GetApp";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import usePermission from "hooks/usePermission";

export default function ParticipantFileReportsList({
  errorsList = [],
  uploadInfo,
  errorSummary,
  criticalErrors = [],
  entriesStart,
  entriesEnd,
  totalData,
  totalPages,
  currentPage,
  handlePageChange,
  handleViewDetail,
  handleExportErrors,
  callHandleClickSubmit,
  handleGoBack,
}) {
  const canListParticipant = usePermission("Listar", "Participantes");

  /**
   * Formatea una fecha para mostrarla en formato legible
   * @param {string} dateString - Fecha en formato string
   * @returns {string} Fecha formateada
   */
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return isNaN(date.getTime())
      ? "Fecha inválida"
      : date.toISOString().slice(0, 19).replace("T", " ");
  };

  /**
   * Obtiene el color del chip según la severidad
   * @param {string} severity - Severidad del error
   * @returns {string} Color del chip
   */
  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case "critical":
      case "crítica":
        return "error";
      case "high":
      case "alta":
        return "warning";
      case "medium":
      case "media":
        return "info";
      case "low":
      case "baja":
        return "success";
      default:
        return "default";
    }
  };

  /**
   * Obtiene el ícono según la severidad
   * @param {string} severity - Severidad del error
   * @returns {JSX.Element} Ícono correspondiente
   */
  const getSeverityIcon = (severity) => {
    switch (severity?.toLowerCase()) {
      case "critical":
      case "crítica":
        return <ErrorIcon fontSize="small" />;
      case "high":
      case "alta":
        return <WarningIcon fontSize="small" />;
      default:
        return <InfoIcon fontSize="small" />;
    }
  };

  /**
   * Calcula el porcentaje de errores por tipo
   * @param {Object} summary - Resumen de errores
   * @returns {Array} Array con porcentajes por tipo
   */
  const calculateErrorPercentages = (summary) => {
    if (!summary || !uploadInfo) return [];

    const total = uploadInfo.error_rows || 1;
    return Object.entries(summary).map(([type, count]) => ({
      type,
      count,
      percentage: Math.round((count / total) * 100),
    }));
  };

  const columns = [
    { Header: "Fila", accessor: "row_number", align: "center" },
    { Header: "Tipo de Error", accessor: "error_type", align: "left" },
    { Header: "Severidad", accessor: "severity", align: "center" },
    { Header: "Campo", accessor: "field_name", align: "left" },
    { Header: "Valor", accessor: "field_value", align: "left" },
    { Header: "Mensaje", accessor: "error_message", align: "left" },
    { Header: "Fecha", accessor: "created", align: "center" },
    { Header: "Acciones", accessor: "action", width: "12%", align: "center", disableSortBy: true },
  ];

  const rows = errorsList.map((error) => ({
    row_number: (
      <MDTypography
        variant="body2"
        style={{ fontSize: "14px", fontFamily: "Roboto, sans-serif", fontWeight: "bold" }}>
        {error.row_number}
      </MDTypography>
    ),
    error_type: (
      <MDTypography variant="body2" style={{ fontSize: "14px", fontFamily: "Roboto, sans-serif" }}>
        {error.error_type_display || error.error_type}
      </MDTypography>
    ),
    severity: (
      <Chip
        icon={getSeverityIcon(error.severity)}
        label={error.severity_display || error.severity}
        color={getSeverityColor(error.severity)}
        size="small"
      />
    ),
    field_name: (
      <MDTypography variant="body2" style={{ fontSize: "14px", fontFamily: "Roboto, sans-serif" }}>
        {error.field_name || "N/A"}
      </MDTypography>
    ),
    field_value: (
      <MDTypography
        variant="body2"
        style={{
          fontSize: "14px",
          fontFamily: "Roboto, sans-serif",
          maxWidth: "150px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={error.field_value}>
        {error.field_value || "Campo vacío"}
      </MDTypography>
    ),
    error_message: (
      <MDTypography
        variant="body2"
        style={{
          fontSize: "14px",
          fontFamily: "Roboto, sans-serif",
          maxWidth: "200px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={error.error_message}>
        {error.error_message}
      </MDTypography>
    ),
    created: (
      <MDTypography variant="body2" style={{ fontSize: "14px", fontFamily: "Roboto, sans-serif" }}>
        {formatDate(error.created)}
      </MDTypography>
    ),
    action: (
      <MDBox style={{ display: "flex", gap: "5px", justifyContent: "center" }}>
        <MDButton
          variant="text"
          color="info"
          size="small"
          onClick={() => handleViewDetail(error)}
          startIcon={<VisibilityIcon />}>
          Detalle
        </MDButton>
      </MDBox>
    ),
  }));

  const errorPercentages = calculateErrorPercentages(errorSummary);

  return (
    <>
      {/* Información del archivo y resumen */}
      {uploadInfo && (
        <Card sx={{ mb: 3, p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <MDTypography variant="h6" gutterBottom>
                Información del Archivo
              </MDTypography>
              <MDTypography variant="body2">
                <strong>Archivo:</strong> {uploadInfo.original_filename}
              </MDTypography>
              <MDTypography variant="body2">
                <strong>Estado:</strong> {uploadInfo.status}
              </MDTypography>
              <MDTypography variant="body2">
                <strong>Total de filas:</strong> {uploadInfo.total_rows}
              </MDTypography>
              <MDTypography variant="body2" style={{ color: "red" }}>
                <strong>Filas con errores:</strong> {uploadInfo.error_rows}
              </MDTypography>
            </Grid>

            {errorPercentages.length > 0 && (
              <Grid item xs={12} md={6}>
                <MDTypography variant="h6" gutterBottom>
                  Distribución de Errores
                </MDTypography>
                {errorPercentages.map((item, index) => (
                  <MDBox key={index} sx={{ mb: 1 }}>
                    <MDBox sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                      <MDTypography variant="body2">{item.type}</MDTypography>
                      <MDTypography variant="body2">
                        {item.count} ({item.percentage}%)
                      </MDTypography>
                    </MDBox>
                    <LinearProgress
                      variant="determinate"
                      value={item.percentage}
                      color={
                        item.percentage > 50 ? "error" : item.percentage > 25 ? "warning" : "info"
                      }
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </MDBox>
                ))}
              </Grid>
            )}
          </Grid>
        </Card>
      )}

      {/* Errores críticos */}
      {criticalErrors.length > 0 && (
        <Card sx={{ mb: 3, p: 2, bgcolor: "#fff3e0" }}>
          <MDTypography variant="h6" gutterBottom color="warning">
            <WarningIcon sx={{ mr: 1, verticalAlign: "middle" }} />
            Errores Críticos ({criticalErrors.length})
          </MDTypography>
          <MDTypography variant="body2">
            Se encontraron {criticalErrors.length} errores críticos que requieren atención
            inmediata.
          </MDTypography>
        </Card>
      )}

      {/* Botones de acción */}
      <MDBox
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "20px",
        }}>
        <MDButton
          onClick={handleGoBack}
          variant="outlined"
          color="info"
          style={{ marginLeft: "20px" }}
          startIcon={<ArrowBackIcon />}>
          Volver
        </MDButton>
        {canListParticipant && (
          <MDButton
            style={{ marginRight: "20px" }}
            onClick={handleExportErrors}
            variant="contained"
            color="success"
            startIcon={<GetAppIcon />}>
            Exportar Errores
          </MDButton>
        )}
      </MDBox>

      {canListParticipant && (
        <>
          <DataTable
            table={{ columns, rows }}
            isSorted={true}
            entriesPerPage={false}
            showTotalEntries={false}
            canSearch={false}
            noEndBorder={false}
          />

          {!errorsList || errorsList.length === 0 ? (
            <MDBox style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
              <MDTypography
                variant="button"
                color="secondary"
                fontWeight="regular"
                style={{ fontSize: "20px", fontFamily: "Roboto, sans-serif" }}>
                No hay errores para mostrar
              </MDTypography>
            </MDBox>
          ) : (
            <>
              <MDBox style={{ marginLeft: "20px" }} mb={{ xs: 3, sm: 0 }}>
                <MDTypography
                  style={{ left: "20px" }}
                  variant="button"
                  color="secondary"
                  fontWeight="regular">
                  Mostrando {entriesStart} hasta {entriesEnd} de {totalData} errores en total
                </MDTypography>
              </MDBox>
              <Stack style={{ marginLeft: "20px", marginBottom: "10px" }} spacing={2}>
                <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} />
              </Stack>
            </>
          )}
        </>
      )}
    </>
  );
}

ParticipantFileReportsList.propTypes = {
  errorsList: PropTypes.array,
  uploadInfo: PropTypes.object,
  errorSummary: PropTypes.object,
  criticalErrors: PropTypes.array,
  entriesStart: PropTypes.number,
  entriesEnd: PropTypes.number,
  totalData: PropTypes.number,
  totalPages: PropTypes.number,
  currentPage: PropTypes.number,
  handlePageChange: PropTypes.func,
  handleViewDetail: PropTypes.func.isRequired,
  handleExportErrors: PropTypes.func.isRequired,
  callHandleClickSubmit: PropTypes.func.isRequired,
  handleGoBack: PropTypes.func.isRequired,
};
